import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, CreditCard, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/platform-billing`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const chf = (n) => 'CHF ' + (Number(n) || 0).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function PlatformDunningBanner() {
  const location = useLocation();
  const [info, setInfo] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await axios.get(API, auth());
        if (!active) return;
        const invoices = r.data?.invoices || [];
        const open = invoices.filter((i) => i.status === 'open' || i.status === 'overdue');
        const overdue = invoices.filter((i) => i.status === 'overdue');
        setInfo({
          openSum: r.data?.open_sum || 0,
          openCount: open.length,
          overdueCount: overdue.length,
          suspended: !!r.data?.state?.billing_suspended,
        });
      } catch { /* kein Zugriff / keine Rechnungen – Banner ausblenden */ }
    })();
    return () => { active = false; };
  }, []);

  // Auf der Rechnungsseite selbst nicht doppelt anzeigen
  if (!info || info.openCount === 0 || location.pathname === '/meine-rechnungen') return null;

  const critical = info.overdueCount > 0 || info.suspended;
  // Nur unkritische (nur "offen") Banner lassen sich für die Sitzung ausblenden
  if (dismissed && !critical) return null;

  const cls = critical
    ? 'border-red-200 bg-red-50 text-red-800'
    : 'border-amber-200 bg-amber-50 text-amber-800';

  return (
    <div className={`mx-4 mt-4 flex flex-col gap-2 rounded-xl border px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between lg:mx-8 ${cls}`} data-testid="platform-dunning-banner">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" />
        <div>
          <span className="font-semibold">
            {info.suspended
              ? 'Dein Zugang ist wegen offener Plattform-Rechnungen eingeschränkt.'
              : critical
                ? `${info.overdueCount} überfällige Plattform-Rechnung${info.overdueCount > 1 ? 'en' : ''}.`
                : `${info.openCount} offene Plattform-Rechnung${info.openCount > 1 ? 'en' : ''}.`}
          </span>{' '}
          <span>Offener Betrag: <strong>{chf(info.openSum)}</strong>.</span>
        </div>
      </div>
      <div className="flex items-center gap-2 self-end sm:self-auto">
        <Link
          to="/meine-rechnungen"
          data-testid="platform-dunning-pay-link"
          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white ${critical ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}`}
        >
          <CreditCard className="h-4 w-4" /> Jetzt begleichen
        </Link>
        {!critical && (
          <button
            data-testid="platform-dunning-dismiss"
            onClick={() => setDismissed(true)}
            title="Ausblenden"
            className="rounded-lg p-1.5 text-amber-700 hover:bg-amber-100"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
